import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { PDFDocument } from 'pdf-lib';
import { supabase, uploadFile } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useOwner } from '../hooks/useOwner';
import { usePlan } from '../hooks/usePlan';
import { buildPlan, recordFor, targetFor, CATEGORY_LABELS, CATEGORY_ORDER, TARGET_LABELS, FOLDER_FOR } from '../utils/importPlan';
import { readFile as readLocal, collectRegs, regFromFileName } from '../utils/localReader';
import { readPages, loadModel } from '../utils/ocrEngine';
import { Upload, FileText, Car, AlertTriangle, CheckCircle2, Eye, X, Sparkles, Cpu } from 'lucide-react';
import toast from 'react-hot-toast';

// Bulk Import — onboarding a client's paperwork in one go.
//   1. Drop PDFs / photos (whole folders of scans, any order, several documents per PDF).
//   2. Reading, one of:
//      • Local (default, free): every page is OCR'd in this browser, typed by FleetPro's own
//        document model and read by rules (utils/localReader). Pages the model isn't sure
//        about can optionally be passed to Claude.
//      • Claude: chunks of a few pages are read by the analyze-import edge function.
//      Either way the result is split into separate documents with the car, type and key fields.
//   3. Everything is shown for review — nothing is saved until "Import".
//   4. Import creates any new cars, cuts each document into its own PDF, uploads it and
//      saves it as the same record the app's own forms would create.

const CHUNK_PAGES = 8;        // pages per reader call (keeps each call well inside the edge time limit)
const PARALLEL = 3;           // reader calls at once
const LOCAL_SURE = 60;        // local reader confidence below this counts as "not sure"
const PREFS_KEY = 'bulkImportReader';
const EXPENSE_CATS = ['Fuel', 'Service', 'Repairs', 'Insurance', 'Road Tax', 'Tyres', 'Cleaning', 'Parking', 'Fines', 'Parts', 'Restoration', 'Other'];

const fmtDate = (d) => (d ? d.split('-').reverse().join('/') : '');

// Photos become one-page PDFs so every document is handled the same way.
async function toPdfBytes(file) {
  const buf = await file.arrayBuffer();
  if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) return buf;
  const doc = await PDFDocument.create();
  const img = /png$/i.test(file.type) || /\.png$/i.test(file.name) ? await doc.embedPng(buf) : await doc.embedJpg(buf);
  const w = 595, h = (img.height / img.width) * w;
  doc.addPage([w, h]).drawImage(img, { x: 0, y: 0, width: w, height: h });
  return doc.save();
}

async function runPool(tasks, n, onDone) {
  let i = 0;
  const worker = async () => { while (i < tasks.length) { const t = tasks[i++]; await t(); onDone?.(); } };
  await Promise.all(Array.from({ length: Math.min(n, tasks.length) }, worker));
}

export const BulkImport = () => {
  const { user } = useAuth();
  const { ownerId } = useOwner();
  const { isReadOnly, isTrialing, vehicleLimit } = usePlan();

  const [step, setStep] = useState('select');       // select | reading | review | importing | done
  const [files, setFiles] = useState([]);           // { name, bytes, pages, error }
  const [progress, setProgress] = useState({ done: 0, total: 0, label: '' });
  const [errors, setErrors] = useState([]);
  const [existing, setExisting] = useState([]);
  const [items, setItems] = useState([]);
  const [proposals, setProposals] = useState({});
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [results, setResults] = useState([]);
  const docCache = useRef({});                       // source index -> loaded PDFDocument
  const pageText = useRef({});                       // "source:page" -> OCR text (for teaching the model)
  const savedPrefs = (() => { try { return JSON.parse(localStorage.getItem(PREFS_KEY)) || {}; } catch { return {}; } })();
  const [reader, setReader] = useState(savedPrefs.reader || 'local');          // local | claude
  const [fallback, setFallback] = useState(savedPrefs.fallback !== false);      // local reader asks Claude when unsure
  useEffect(() => { try { localStorage.setItem(PREFS_KEY, JSON.stringify({ reader, fallback })); } catch { /* private mode */ } }, [reader, fallback]);

  const loadExisting = useCallback(async () => {
    const { data } = await supabase.from('vehicles')
      .select('id, registration_number, vin, brand, model, year, classic_id, classic_id_doc_url, immobilization_doc_url');
    setExisting(data || []);
    return data || [];
  }, []);
  useEffect(() => { loadExisting(); }, [loadExisting]);

  // Don't lose a review by closing the tab.
  useEffect(() => {
    if (!['reading', 'review', 'importing'].includes(step)) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [step]);

  const onDrop = useCallback(async (accepted) => {
    const added = [];
    for (const f of accepted) {
      try {
        const bytes = await toPdfBytes(f);
        const pdf = await PDFDocument.load(bytes, { ignoreEncryption: true });
        added.push({ name: f.name, bytes, pages: pdf.getPageCount() });
      } catch (err) {
        added.push({ name: f.name, bytes: null, pages: 0, error: 'Could not open this file' });
      }
    }
    setFiles(prev => [...prev, ...added]);
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, multiple: true, disabled: step !== 'select',
    accept: { 'application/pdf': ['.pdf'], 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'] },
  });

  const totalPages = files.reduce((s, f) => s + (f.pages || 0), 0);

  const getDoc = async (sourceIdx) => {
    if (!docCache.current[sourceIdx]) docCache.current[sourceIdx] = await PDFDocument.load(files[sourceIdx].bytes, { ignoreEncryption: true });
    return docCache.current[sourceIdx];
  };

  // A new PDF made of the given pages, in order (pages can come from different files).
  const buildPdf = async (pageRefs) => {
    const out = await PDFDocument.create();
    for (const ref of pageRefs) {
      const src = await getDoc(ref.source);
      const [p] = await out.copyPages(src, [ref.page - 1]);
      out.addPage(p);
    }
    return out.save();
  };

  // ---- 2. Read ---------------------------------------------------------------
  // One chunk of consecutive pages → Claude → documents (page numbers mapped back to the file).
  const readChunkWithClaude = async (c, batch, tempPaths) => {
    const pageRefs = [];
    for (let p = c.from; p <= c.to; p++) pageRefs.push({ source: c.f.i, page: p });
    const bytes = await buildPdf(pageRefs);
    const up = await uploadFile(new File([bytes], `${batch}.pdf`, { type: 'application/pdf' }), `imports/${batch}`);
    tempPaths.push(up.path);
    const { data, error } = await supabase.functions.invoke('analyze-import', {
      body: { fileUrl: up.url, fileName: c.f.name, pageCount: c.to - c.from + 1 },
    });
    if (error || data?.error) throw new Error(data?.error || error?.message || 'Reader failed');
    return data.documents.map(d => ({
      ...d, reader: 'claude', sourceIdx: c.f.i, sourceName: c.f.name,
      pageRefs: d.pages.map(p => ({ source: c.f.i, page: c.from + p - 1 })),
    }));
  };

  // Pages that could not be read at all still come through, as unsorted documents.
  const unreadPages = (f, from, to, why) => {
    const out = [];
    for (let p = from; p <= to; p++) out.push({
      sourceIdx: f.i, sourceName: f.name, pageRefs: [{ source: f.i, page: p }], reader: 'none',
      category: 'other', registration: '', vin: '', title: `${f.name.replace(/\.pdf$/i, '')} page ${p}`,
      date: '', reference: '', confidence: 0, summary: why, fields: {}, vehicle: {},
    });
    return out;
  };

  const runClaudeChunks = async (chunks, errs, label) => {
    const batch = `${Date.now()}`;
    const tempPaths = [];
    const docs = [];
    setProgress({ done: 0, total: chunks.length, label });
    const tasks = chunks.map(c => async () => {
      try { docs.push(...await readChunkWithClaude(c, batch, tempPaths)); }
      catch (err) {
        errs.push(`${c.f.name} pages ${c.from}-${c.to}: ${err.message}`);
        docs.push(...unreadPages(c.f, c.from, c.to, 'Could not be read automatically'));
      }
    });
    await runPool(tasks, PARALLEL, () => setProgress(p => ({ ...p, done: p.done + 1 })));
    // The chunks were only needed by the reader — don't leave copies lying around.
    if (tempPaths.length) supabase.storage.from('vehicle-files').remove(tempPaths).catch(() => {});
    return docs;
  };

  const readWithClaude = async (usable, errs) => {
    const chunks = [];
    for (const f of usable) for (let s = 1; s <= f.pages; s += CHUNK_PAGES) chunks.push({ f, from: s, to: Math.min(f.pages, s + CHUNK_PAGES - 1) });
    return runClaudeChunks(chunks, errs, 'Reading documents with Claude');
  };

  const readOnThisComputer = async (usable, errs, ex) => {
    setProgress({ done: 0, total: usable.reduce((s, f) => s + f.pages, 0), label: 'Reading pages on this computer' });
    const model = await loadModel();
    const texts = await readPages(usable.map(f => ({ idx: f.i, bytes: f.bytes, pages: f.pages })),
      () => setProgress(p => ({ ...p, done: p.done + 1 })));
    for (const f of usable) for (const pg of texts[f.i] || []) pageText.current[`${f.i}:${pg.page}`] = pg.text;

    // Registrations the reader should recognise: the account's cars, ones printed next to a
    // registration label anywhere in this batch, and ones in the file names.
    const allTexts = usable.flatMap(f => (texts[f.i] || []).map(p => p.text));
    const known = [
      ...ex.map(v => v.registration_number).filter(Boolean),
      ...collectRegs(allTexts),
      ...usable.map(f => regFromFileName(f.name)).filter(Boolean),
    ];
    let docs = [];
    for (const f of usable) {
      const pages = texts[f.i] || [];
      const failed = pages.filter(p => p.error).map(p => p.page);
      if (failed.length) errs.push(`${f.name}: page(s) ${failed.join(', ')} could not be opened`);
      for (const d of readLocal(pages.filter(p => !p.error), model, { fileName: f.name, knownRegs: known })) {
        docs.push({ ...d, sourceIdx: f.i, sourceName: f.name, pageRefs: d.pages.map(p => ({ source: f.i, page: p })) });
      }
      for (const p of failed) docs.push(...unreadPages(f, p, p, 'Could not be opened'));
    }

    // Second opinion for the pages the local reader isn't sure about.
    const unsure = docs.filter(d => d.confidence < LOCAL_SURE);
    if (fallback && unsure.length) {
      const chunks = [];
      for (const f of usable) {
        const pages = [...new Set(unsure.filter(d => d.sourceIdx === f.i).flatMap(d => d.pageRefs.map(r => r.page)))].sort((a, b) => a - b);
        let run = [];
        const flush = () => { if (run.length) chunks.push({ f, from: run[0], to: run[run.length - 1] }); run = []; };
        for (const p of pages) {
          if (run.length && (p !== run[run.length - 1] + 1 || run.length >= CHUNK_PAGES)) flush();
          run.push(p);
        }
        flush();
      }
      const claudeErrs = [];
      const second = await runClaudeChunks(chunks, claudeErrs, `Asking Claude about ${unsure.length} unclear document(s)`);
      const answered = new Set(second.filter(d => d.reader === 'claude').flatMap(d => d.pageRefs.map(r => `${r.source}:${r.page}`)));
      docs = [
        ...docs.filter(d => !d.pageRefs.some(r => answered.has(`${r.source}:${r.page}`))),
        ...second.filter(d => d.reader === 'claude'),
      ];
      if (claudeErrs.length) errs.push(`Claude could not check ${claudeErrs.length} part(s) — the local reading was kept for those pages.`);
    }
    return docs;
  };

  const startReading = async () => {
    const usable = files.map((f, i) => ({ ...f, i })).filter(f => f.bytes && f.pages);
    if (!usable.length) return;
    setStep('reading'); setErrors([]);
    pageText.current = {};
    const errs = [];
    let docs;
    try {
      const ex = await loadExisting();
      docs = reader === 'claude' ? await readWithClaude(usable, errs) : await readOnThisComputer(usable, errs, ex);
    } catch (err) {
      toast.error(`Reading failed: ${err.message}`);
      setStep('select');
      return;
    }
    let n = 0;
    docs = docs.map(d => ({ ...d, id: `d${++n}`, predicted: d.category }));
    docs.sort((a, b) => a.sourceIdx - b.sourceIdx || a.pageRefs[0].page - b.pageRefs[0].page);
    const ex = await loadExisting();
    const plan = buildPlan(docs, ex);
    setItems(plan.items);
    setProposals(plan.proposals);
    setErrors(errs);
    setStep('review');
  };

  // ---- 3. Review -------------------------------------------------------------
  const carOptions = useMemo(() => [
    ...existing.map(v => ({ key: 'v:' + v.id, label: `${v.registration_number} — ${v.brand || ''} ${v.model || ''}` })),
    ...Object.values(proposals).map(p => ({ key: p.key, label: `${p.registration_number} — ${p.brand} ${p.model} (new)` })),
  ], [existing, proposals]);
  const carLabel = (key) => carOptions.find(o => o.key === key)?.label || 'No car chosen';

  const updateItem = (id, patch) => setItems(prev => prev.map(it => {
    if (it.id !== id) return it;
    const next = { ...it, ...patch, fields: { ...it.fields, ...(patch.fields || {}) } };
    next.target = targetFor(next);
    return next;
  }));
  const updateProposal = (key, patch) => setProposals(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const addCar = () => {
    const reg = (window.prompt('Registration of the new car?') || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!reg) return;
    const key = 'n:' + reg;
    if (proposals[key] || existing.some(v => (v.registration_number || '').toUpperCase().replace(/[^A-Z0-9]/g, '') === reg)) { toast.error('That car is already in the list'); return; }
    setProposals(prev => ({ ...prev, [key]: { key, create: true, registration_number: reg, brand: '', model: '', variant: '', body_style: '', year: '', color: '', fuel_type: 'Petrol', vin: '', engine_number: '', engine_size: '', mileage: '', status: 'classic', notes: 'Created by Bulk Import.' } }));
  };

  const preview = async (it) => {
    const w = window.open('', '_blank');
    const bytes = await buildPdf(it.pageRefs);
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    if (w) w.location.href = url; else window.open(url, '_blank');
  };

  const included = items.filter(i => i.include && i.target !== 'skip');
  const issues = (it) => it.include && (!it.vehicleKey || it.flags.some(f => /check|Pick/.test(f)));
  const usedNewCars = Object.values(proposals).filter(p => p.create && included.some(i => i.vehicleKey === p.key));
  const groups = useMemo(() => {
    const g = {};
    for (const it of items) {
      if (onlyIssues && !issues(it)) continue;
      (g[it.vehicleKey || ''] = g[it.vehicleKey || ''] || []).push(it);
    }
    return Object.entries(g).sort(([a], [b]) => (a === '' ? -1 : b === '' ? 1 : carLabel(a).localeCompare(carLabel(b))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, onlyIssues, carOptions]);

  // ---- 4. Import ---------------------------------------------------------------
  const runImport = async () => {
    const missingCar = included.filter(i => !i.vehicleKey);
    if (missingCar.length) { toast.error(`${missingCar.length} document(s) have no car — pick one or untick them`); setOnlyIssues(true); return; }
    const blocked = included.filter(i => i.vehicleKey.startsWith('n:') && !proposals[i.vehicleKey]?.create);
    if (blocked.length) { toast.error('Some documents belong to a new car that is set not to be created'); return; }
    for (const p of usedNewCars) if (!p.brand || !p.model) { toast.error(`Fill in make and model for ${p.registration_number}`); return; }
    if (!isTrialing && existing.length + usedNewCars.length > vehicleLimit) {
      toast.error(`This would take the account to ${existing.length + usedNewCars.length} cars; the plan allows ${vehicleLimit}.`); return;
    }
    if (!window.confirm(`Import ${included.length} documents and create ${usedNewCars.length} new car(s)?`)) return;

    setStep('importing');
    const res = [];
    const total = usedNewCars.length + included.length;
    setProgress({ done: 0, total, label: 'Importing' });
    const tick = () => setProgress(p => ({ ...p, done: p.done + 1 }));

    // a) New cars
    const keyToId = {};
    for (const v of existing) keyToId['v:' + v.id] = v.id;
    const vehicleRows = {};
    for (const v of existing) vehicleRows[v.id] = v;
    for (const p of usedNewCars) {
      const row = {
        user_id: ownerId, registration_number: p.registration_number, brand: p.brand, model: p.model,
        variant: p.variant || null, body_style: p.body_style || null, year: p.year ? parseInt(p.year, 10) : null,
        color: p.color || null, fuel_type: p.fuel_type || 'Petrol', vin: p.vin || null, engine_number: p.engine_number || null,
        engine_size: p.engine_size || null, mileage: p.mileage ? parseInt(p.mileage, 10) : 0, ownership_status: 'Owned',
        status: p.status || 'classic', notes: p.notes || null,
      };
      const { data, error } = await supabase.from('vehicles').insert(row).select().single();
      if (error) res.push({ ok: false, label: `Car ${p.registration_number}`, msg: error.message });
      else { keyToId[p.key] = data.id; vehicleRows[data.id] = data; res.push({ ok: true, label: `Car ${p.registration_number} created` }); }
      tick();
    }

    // b) Documents
    const slotDocs = {};   // vehicleId|slot -> [{item, url, path}]
    for (const it of included) {
      const vehicleId = keyToId[it.vehicleKey];
      if (!vehicleId) { res.push({ ok: false, label: it.title, msg: 'Its car was not created' }); tick(); continue; }
      try {
        const bytes = await buildPdf(it.pageRefs);
        const up = await uploadFile(new File([bytes], `${it.title}.pdf`, { type: 'application/pdf' }), FOLDER_FOR[it.target] || 'documents');
        if (it.target === 'classic_id' || it.target === 'immobilization') {
          (slotDocs[vehicleId + '|' + it.target] = slotDocs[vehicleId + '|' + it.target] || []).push({ it, ...up });
          res.push({ ok: true, label: it.title, msg: CATEGORY_LABELS[it.category] });
        } else {
          const rec = recordFor(it, { vehicleId, ownerId, userId: user.id, url: up.url, path: up.path });
          const { error } = await supabase.from(rec.table).insert(rec.row);
          if (error) throw error;
          res.push({ ok: true, label: it.title, msg: TARGET_LABELS[it.target] });
        }
      } catch (err) {
        res.push({ ok: false, label: it.title, msg: err.message });
      }
      tick();
    }

    // c) Classic ID / immobilization: one current document per car, the rest under "Previous versions".
    for (const [k, list] of Object.entries(slotDocs)) {
      const [vehicleId, slot] = k.split('|');
      const urlField = slot === 'classic_id' ? 'classic_id_doc_url' : 'immobilization_doc_url';
      const pathField = slot === 'classic_id' ? 'classic_id_doc_path' : 'immobilization_doc_path';
      list.sort((a, b) => String(b.it.fields.start_date || b.it.fields.issue_date || b.it.date || '').localeCompare(String(a.it.fields.start_date || a.it.fields.issue_date || a.it.date || '')));
      let rest = list;
      const v = vehicleRows[vehicleId] || {};
      if (!v[urlField]) {
        const [cur, ...others] = list;
        const patch = { [urlField]: cur.url, [pathField]: cur.path };
        if (slot === 'classic_id' && !v.classic_id && cur.it.fields.classic_id) patch.classic_id = cur.it.fields.classic_id;
        const { error } = await supabase.from('vehicles').update(patch).eq('id', vehicleId);
        if (error) res.push({ ok: false, label: cur.it.title, msg: error.message });
        rest = others;
      }
      for (const r of rest) {
        const { error } = await supabase.from('file_archive').insert({ vehicle_id: vehicleId, slot, url: r.url, file_path: r.path, label: r.it.title });
        if (error) res.push({ ok: false, label: r.it.title, msg: error.message });
      }
    }

    // d) Teach the document model: every confirmed page with the type it was imported as.
    //    (Read only by the retraining script with the service key — see scripts/train_doc_classifier.py.)
    const training = [];
    for (const it of included) {
      for (const r of it.pageRefs) {
        const text = pageText.current[`${r.source}:${r.page}`];
        if (!text || text.replace(/\s/g, '').length < 25) continue;
        training.push({
          user_id: user.id, owner_id: ownerId, category: it.category, predicted: it.predicted || null,
          confidence: it.confidence ?? null, reader: it.reader || null, file_name: it.sourceName, page: r.page,
          page_text: text.slice(0, 20000),
        });
      }
    }
    for (let i = 0; i < training.length; i += 50) {
      const { error } = await supabase.from('import_training').insert(training.slice(i, i + 50));
      if (error) { console.warn('import_training:', error.message); break; }
    }

    setResults(res);
    setStep('done');
    loadExisting();
  };

  const reset = () => { setFiles([]); setItems([]); setProposals({}); setResults([]); setErrors([]); docCache.current = {}; pageText.current = {}; setStep('select'); };

  // ---- UI ------------------------------------------------------------------------
  if (isReadOnly) return <div className="page"><div className="card">This account is read-only, so nothing can be imported.</div></div>;

  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Bulk Import</div>
          <div className="page-subtitle">Drop a client's scanned paperwork — it is split, sorted by car and read for you to check before anything is saved.</div>
        </div>
        {step !== 'select' && step !== 'reading' && step !== 'importing' && (
          <button className="btn btn-ghost" onClick={() => { if (step === 'done' || window.confirm('Start again? This review will be lost.')) reset(); }}>Start again</button>
        )}
      </div>

      {step === 'select' && (
        <div className="card">
          <div {...getRootProps()} style={{ border: '2px dashed var(--border)', borderRadius: 12, padding: 36, textAlign: 'center', cursor: 'pointer', background: isDragActive ? 'var(--bg3)' : 'transparent' }}>
            <input {...getInputProps()} />
            <Upload size={28} style={{ color: 'var(--text3)' }} />
            <div style={{ fontWeight: 600, marginTop: 8 }}>Drop PDFs or photos here, or click to choose</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>Any order, any number of documents per PDF. JPG/PNG photos are fine too.</div>
          </div>
          {files.length > 0 && (
            <>
              <div className="table-wrap" style={{ marginTop: 16 }}>
                <table>
                  <thead><tr><th>File</th><th>Pages</th><th></th></tr></thead>
                  <tbody>{files.map((f, i) => (
                    <tr key={i}>
                      <td className="td-primary"><FileText size={13} /> {f.name}{f.error && <span className="badge badge-red" style={{ marginLeft: 8 }}>{f.error}</span>}</td>
                      <td>{f.pages || '—'}</td>
                      <td><button className="btn btn-ghost btn-sm" onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}><X size={12} /></button></td>
                    </tr>))}</tbody>
                </table>
              </div>
              <div style={{ marginTop: 16, display: 'grid', gap: 8 }}>
                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', fontSize: 13 }}>
                  <input type="radio" name="reader" checked={reader === 'local'} onChange={() => setReader('local')} style={{ marginTop: 3 }} />
                  <span><b><Cpu size={13} /> Read on this computer</b> — free. Text recognition and FleetPro's own document model run in this browser
                    (about {Math.max(1, Math.round(totalPages * 4 / 60))} min for {totalPages} pages).</span>
                </label>
                {reader === 'local' && (
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: 12, marginLeft: 24, color: 'var(--text3)' }}>
                    <input type="checkbox" checked={fallback} onChange={e => setFallback(e.target.checked)} />
                    Ask Claude about documents the local reader isn't sure of (paid, only those pages)
                  </label>
                )}
                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', fontSize: 13 }}>
                  <input type="radio" name="reader" checked={reader === 'claude'} onChange={() => setReader('claude')} style={{ marginTop: 3 }} />
                  <span><b><Sparkles size={13} /> Read everything with Claude</b> — most accurate, paid (about {Math.ceil(totalPages / CHUNK_PAGES)} reader calls).</span>
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, flexWrap: 'wrap', gap: 10 }}>
                <span style={{ fontSize: 13, color: 'var(--text3)' }}>{files.length} files · {totalPages} pages</span>
                <button className="btn btn-primary" disabled={!totalPages} onClick={startReading}>{reader === 'local' ? <Cpu size={14} /> : <Sparkles size={14} />} Read and sort</button>
              </div>
            </>
          )}
        </div>
      )}

      {(step === 'reading' || step === 'importing') && (
        <div className="card">
          <div style={{ fontWeight: 600, marginBottom: 10 }}>{progress.label}… {progress.done} / {progress.total}</div>
          <div style={{ height: 8, background: 'var(--bg3)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: pct + '%', height: '100%', background: 'linear-gradient(90deg,#4f8ef7,#7c3aed)', transition: 'width .3s' }} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8 }}>Keep this tab open.</div>
        </div>
      )}

      {step === 'review' && (
        <>
          {errors.length > 0 && (
            <div className="card" style={{ marginBottom: 16, borderColor: '#f97316' }}>
              <div style={{ fontWeight: 600, color: '#f97316', marginBottom: 6 }}><AlertTriangle size={14} /> Some pages could not be read automatically — they are listed as "Other document" for you to sort:</div>
              {errors.map((e, i) => <div key={i} style={{ fontSize: 12, color: 'var(--text3)' }}>{e}</div>)}
            </div>
          )}

          <div className="stats-grid" style={{ marginBottom: 16 }}>
            <div className="stat-card"><div className="stat-label">Documents found</div><div style={{ fontSize: 22, fontWeight: 700 }}>{items.length}</div></div>
            <div className="stat-card"><div className="stat-label">To import</div><div style={{ fontSize: 22, fontWeight: 700 }}>{included.length}</div></div>
            <div className="stat-card"><div className="stat-label">New cars</div><div style={{ fontSize: 22, fontWeight: 700 }}>{usedNewCars.length}</div></div>
            <div className="stat-card"><div className="stat-label">Need checking</div><div style={{ fontSize: 22, fontWeight: 700, color: items.filter(issues).length ? '#f97316' : undefined }}>{items.filter(issues).length}</div></div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div className="card-title" style={{ margin: 0 }}><Car size={15} /> New cars</div>
              <button className="btn btn-ghost btn-sm" onClick={addCar}>+ Add a car</button>
            </div>
            {Object.values(proposals).length === 0 ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>Every document matched a car already in the account.</div> : (
              <div className="table-wrap"><table>
                <thead><tr><th>Create</th><th>Registration</th><th>Make</th><th>Model</th><th>Year</th><th>Colour</th><th>VIN / chassis</th><th>Status</th><th>Docs</th></tr></thead>
                <tbody>{Object.values(proposals).map(p => (
                  <tr key={p.key}>
                    <td><input type="checkbox" checked={p.create} onChange={e => updateProposal(p.key, { create: e.target.checked })} /></td>
                    <td><input className="form-input" style={{ width: 110 }} value={p.registration_number} onChange={e => updateProposal(p.key, { registration_number: e.target.value.toUpperCase() })} /></td>
                    <td><input className="form-input" style={{ width: 120 }} value={p.brand} onChange={e => updateProposal(p.key, { brand: e.target.value })} /></td>
                    <td><input className="form-input" style={{ width: 140 }} value={p.model} onChange={e => updateProposal(p.key, { model: e.target.value })} /></td>
                    <td><input className="form-input" style={{ width: 70 }} value={p.year} onChange={e => updateProposal(p.key, { year: e.target.value.replace(/\D/g, '') })} /></td>
                    <td><input className="form-input" style={{ width: 90 }} value={p.color} onChange={e => updateProposal(p.key, { color: e.target.value })} /></td>
                    <td><input className="form-input" style={{ width: 170 }} value={p.vin} onChange={e => updateProposal(p.key, { vin: e.target.value.toUpperCase() })} /></td>
                    <td><select className="form-input" value={p.status} onChange={e => updateProposal(p.key, { status: e.target.value })}>
                      {['classic', 'active', 'restoration', 'immobilized', 'inactive'].map(s => <option key={s} value={s}>{s}</option>)}</select></td>
                    <td>{items.filter(i => i.vehicleKey === p.key && i.include).length}</td>
                  </tr>))}</tbody>
              </table></div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 10 }}>
            <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" checked={onlyIssues} onChange={e => setOnlyIssues(e.target.checked)} /> Show only documents that need checking
            </label>
            <button className="btn btn-primary" onClick={runImport}><CheckCircle2 size={14} /> Import {included.length} documents</button>
          </div>

          {groups.map(([key, list]) => (
            <div key={key || 'none'} className="card" style={{ marginBottom: 14 }}>
              <div className="card-title" style={{ color: key ? undefined : '#f97316' }}>{key ? carLabel(key) : 'No car found — choose one for each document'} <span style={{ fontWeight: 400, color: 'var(--text3)', fontSize: 12 }}>· {list.length}</span></div>
              <div className="table-wrap"><table>
                <thead><tr><th></th><th>Document</th><th>Type</th><th>Car</th><th>Details</th><th></th></tr></thead>
                <tbody>{list.map(it => (
                  <tr key={it.id} style={{ opacity: it.include ? 1 : 0.5 }}>
                    <td><input type="checkbox" checked={it.include} onChange={e => updateItem(it.id, { include: e.target.checked })} /></td>
                    <td style={{ minWidth: 220 }}>
                      <input className="form-input" value={it.title} onChange={e => updateItem(it.id, { title: e.target.value })} />
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                        {it.sourceName} · p.{it.pageRefs.map(r => r.page).join(',')}{it.confidence ? ` · ${it.confidence}% sure` : ''}{it.reader === 'claude' ? ' · Claude' : it.reader === 'local' ? ' · local' : ''} · → {TARGET_LABELS[it.target]}
                      </div>
                      {it.flags.map((f, i) => <div key={i} style={{ fontSize: 11, color: '#f97316' }}><AlertTriangle size={10} /> {f}</div>)}
                    </td>
                    <td><select className="form-input" value={it.category} onChange={e => updateItem(it.id, { category: e.target.value, include: e.target.value !== 'blank' })}>
                      {CATEGORY_ORDER.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}</select></td>
                    <td><select className="form-input" style={{ maxWidth: 220 }} value={it.vehicleKey} onChange={e => updateItem(it.id, { vehicleKey: e.target.value })}>
                      <option value="">— choose —</option>
                      {carOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}</select></td>
                    <td><Details it={it} onChange={(fields) => updateItem(it.id, { fields })} /></td>
                    <td><button className="btn btn-ghost btn-sm" title="Preview" onClick={() => preview(it)}><Eye size={13} /></button></td>
                  </tr>))}</tbody>
              </table></div>
            </div>
          ))}
        </>
      )}

      {step === 'done' && (
        <div className="card">
          <div className="card-title">
            {results.filter(r => !r.ok).length ? <><AlertTriangle size={15} color="#f97316" /> Imported with {results.filter(r => !r.ok).length} problem(s)</> : <><CheckCircle2 size={15} color="#22c55e" /> Import complete</>}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 10 }}>{results.filter(r => r.ok).length} saved. Problems are listed first — add those by hand on the car's page.</div>
          <div className="table-wrap"><table><tbody>
            {[...results].sort((a, b) => Number(a.ok) - Number(b.ok)).map((r, i) => (
              <tr key={i}><td>{r.ok ? <span className="badge badge-green">Saved</span> : <span className="badge badge-red">Failed</span>}</td><td className="td-primary">{r.label}</td><td style={{ color: 'var(--text3)' }}>{r.msg}</td></tr>
            ))}
          </tbody></table></div>
        </div>
      )}
    </div>
  );
};

// The few fields that matter for each type, editable inline.
const Details = ({ it, onChange }) => {
  const f = it.fields || {};
  const inp = (key, ph, type = 'text', w = 120) => (
    <input className="form-input" style={{ width: w }} type={type} placeholder={ph} value={f[key] ?? ''}
      onChange={e => onChange({ [key]: type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value })} />
  );
  const row = (children) => <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>{children}</div>;
  switch (it.category) {
    case 'title_deed': return row(<>{inp('owner_name', 'Owner', 'text', 150)}{inp('issue_date', '', 'date', 140)}</>);
    case 'insurance': return row(<>{inp('insurance_company', 'Insurer', 'text', 140)}{inp('expiry_date', '', 'date', 140)}{inp('cost', '€', 'number', 80)}</>);
    case 'road_tax': return row(<>{inp('expiry_date', '', 'date', 140)}{inp('amount_paid', '€', 'number', 80)}</>);
    case 'mot': return row(<>{inp('report_date', '', 'date', 140)}<span style={{ fontSize: 11, color: 'var(--text3)' }}>due</span>{inp('next_due_date', '', 'date', 140)}{inp('cost', '€', 'number', 70)}</>);
    case 'classic_id': return row(<>{inp('classic_id', 'FIVA no.', 'text', 120)}</>);
    case 'immobilization': return row(<>{inp('start_date', '', 'date', 140)}</>);
    case 'expense': return row(<>
      {inp('expense_date', '', 'date', 140)}{inp('amount', '€', 'number', 90)}{inp('supplier', 'Supplier', 'text', 140)}
      <select className="form-input" style={{ width: 110 }} value={f.category || 'Other'} onChange={e => onChange({ category: e.target.value })}>
        {EXPENSE_CATS.map(c => <option key={c}>{c}</option>)}</select>
      {f.is_credit_note && <span className="badge badge-yellow">credit note</span>}
    </>);
    default: return <span style={{ fontSize: 12, color: 'var(--text3)' }}>{fmtDate(it.date)} {it.summary}</span>;
  }
};
